import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { SsoLoginRequest, SsoLoginResponse } from '@/api';
import { useNullableTokens } from '@/common/providers/tokens-provider';
import { useOpenApi } from '@/common/providers/open-api-provider';
import {
    getBrowserTimezone,
    getFallbackErrorKey,
    isApiError
} from '@/common/util';
import { getAppCheckLimitedUseTokenHeaders, isFirebaseError } from '@/firebase';
import { UIToast } from '@/ui';
import { useMeTranslation } from '../hooks';
import { SsoIdToken, SsoProviderId } from '../util';

/** The legal agreements the API asks for when a sign-in creates the account. */
export type SsoConsent = {
    termsOfService: true;
    privacyPolicy: true;
    over16: true;
    contactOptIn: boolean;
};

export type SsoLoginVariables = {
    provider: SsoProviderId;
    /** The token from the provider, replayed as-is on the consent retry. */
    token: SsoIdToken;
    /** Supplied on the consent retry only; ignored by the API when signing in to an existing account. */
    consent?: SsoConsent;
};

type UseSsoLoginActionProps = {
    /**
     * Runs when the sign-in would create an account, so the caller can collect
     * consent and call again with it — passing back the same token, so the
     * provider is not shown a second time.
     */
    onConsentRequired?: (params: {
        provider: SsoProviderId;
        token: SsoIdToken;
    }) => void;
    /**
     * Runs when the API rejected the token. Unlike native, the browser cannot
     * quietly mint a replacement — Google's flow needs a click and a popup
     * opened outside a user gesture is blocked — so the caller has to send the
     * user back to the buttons to start again.
     */
    onTokenRejected?: () => void;
};

type UseSsoLoginActionData = {
    ssoLogin: (variables: SsoLoginVariables) => Promise<SsoLoginResponse>;
    loading: boolean;
    /** Which provider is mid-exchange, so its button can show a spinner. */
    pendingProvider: SsoProviderId | null;
    error: string;
    /** Clears the message so a fresh attempt starts on a clean form. */
    clearError: () => void;
};

/**
 * Signing in with a provider identity, creating the account if there is none
 * yet. Storing the tokens is what actually signs the user in.
 */
export function useSsoLoginAction(
    props: UseSsoLoginActionProps = {}
): UseSsoLoginActionData {
    const { onConsentRequired, onTokenRejected } = props;

    const { t } = useMeTranslation();
    const { setTokens } = useNullableTokens();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    // Which provider is in flight, not merely whether one is — the sign-in
    // buttons show a spinner on the one that was clicked.
    const [pendingProvider, setPendingProvider] =
        useState<SsoProviderId | null>(null);

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error `POST /v3/auth/sso`
    // declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            // Thrown before the request, while minting the App Check token.
            if (isFirebaseError(error)) {
                return t('shared.errors.app_check_failed');
            }

            if (isApiError(error, 'APP_CHECK_FAILED')) {
                return t('shared.errors.app_check_failed');
            }
            if (isApiError(error, 'INVALID_SSO_TOKEN')) {
                return t('actions.sso_login.error_invalid_token');
            }
            if (isApiError(error, 'SSO_EMAIL_NOT_VERIFIED')) {
                return t('actions.sso_login.error_email_not_verified');
            }
            if (isApiError(error, 'SSO_ACCOUNT_TYPE_NOT_SUPPORTED')) {
                return t('actions.sso_login.error_account_type_not_supported');
            }
            if (isApiError(error, 'SSO_IDENTITY_ALREADY_LINKED')) {
                return t('actions.sso_login.error_identity_already_linked');
            }
            if (isApiError(error, 'SSO_PROVIDER_NOT_CONFIGURED')) {
                return t('actions.sso_login.error_provider_not_configured');
            }
            // Both are declared by the endpoint: the address can be blocked,
            // and an account can already exist that the provider identity
            // could not be linked to.
            if (isApiError(error, 'EMAIL_BLOCKED')) {
                return t('shared.errors.email_blocked');
            }
            if (isApiError(error, 'USER_ALREADY_EXISTS')) {
                return t('actions.sso_login.error_user_already_exists');
            }
            if (isApiError(error, 'IP_BLOCKED')) {
                return t('shared.errors.ip_blocked');
            }
            if (isApiError(error, 'TOO_MANY_REQUESTS')) {
                return t('shared.errors.too_many_requests');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: ({ provider, token, consent }: SsoLoginVariables) => {
            setPendingProvider(provider);

            const body: SsoLoginRequest = {
                provider,
                idToken: token.idToken,
                nonce: token.nonce,
                timezone: getBrowserTimezone(),
                ...consent
            };

            // The limited-use token, not the cached one: this call can create
            // an account, and the consent retry is a second call that a
            // replayed App Check token would fail.
            return getAppCheckLimitedUseTokenHeaders()
                .then((headers) =>
                    openApiRef.current.auth.ssoLogin(body, { headers })
                )
                .then((response) => response.data);
        },
        onSuccess: (data) => {
            const { accessToken, refreshToken } = data.data;
            setTokens({ accessToken, refreshToken });
        },
        onError: (error, variables) => {
            // Not a failure: the account does not exist yet, so consent is
            // collected and the sign-in run again with it.
            if (!variables.consent && isApiError(error, 'CONSENT_REQUIRED')) {
                onConsentRequired?.({
                    provider: variables.provider,
                    token: variables.token
                });
                return;
            }

            UIToast.error(getErrorMessage(error));

            if (isApiError(error, 'INVALID_SSO_TOKEN')) {
                onTokenRejected?.();
            }
        },
        onSettled: () => {
            setPendingProvider(null);
        }
    });

    return {
        ssoLogin: (variables) => mutation.mutateAsync(variables),
        loading: pendingProvider !== null,
        pendingProvider,
        error: mutation.error ? getErrorMessage(mutation.error) : '',
        clearError: () => mutation.reset()
    };
}
